import { el, icon } from '../../lib/dom';
import { trapFocus } from '../../lib/ds';

/**
 * Lupina modala (`.cm-scrim` / `.cm-box` iz DS) z vsem, kar mora modal znati:
 * ujeti fokus, zapreti se z Esc in s klikom na scrim, ter fokus vrniti tja,
 * od koder je uporabnik prišel.
 *
 * Zakaj skupen modul in ne kopija v vsakem dialogu: poslušalca za `Escape`
 * in `pointerdown` sta **na dokumentu**. Vsaka kopija bi jih prijavila znova
 * in Esc bi zaprl več modalov hkrati. Stanje »kateri modal je odprt« je
 * torej nujno eno samo.
 */

export interface Modal {
  scrim: HTMLDivElement;
  box: HTMLDivElement;
  title: HTMLHeadingElement;
  close: HTMLButtonElement;
  body: HTMLDivElement;
  cancel: HTMLButtonElement;
  confirm: HTMLButtonElement;
  /** Odpre modal in postavi začetni fokus. */
  show(focus: HTMLElement): void;
  hide(): void;
}

/* ── Skupno stanje ─────────────────────────────────────────────────── */

let openModal: Modal | null = null;
let release: (() => void) | null = null;
let opener: HTMLElement | null = null;

/**
 * Zadnji gumb, na katerega je uporabnik pritisnil.
 *
 * ⚠️ **`document.activeElement` na iOS ne zadošča.** Safari na dotik gumbu
 * fokusa ne da — po tapu ostane `<body>` ali `<main>`. Fokus bi se ob zaprtju
 * modala vrnil na začetek zaslona in uporabnik bi se moral do vrstice
 * pretabulirati nazaj.
 */
let lastPointerTrigger: HTMLElement | null = null;

/**
 * Ali je element resničen kontrolnik, na katerega se sme vrniti fokus.
 *
 * `<main class="nw-screen">` je zaradi skoka na vsebino fokusabilen in ob
 * odpiranju modala na iOS pogosto **je** `activeElement`. Vrnitev nanj ni
 * napaka, ki bi se videla — je le tiho izgubljeno mesto v seznamu.
 */
function isControl(node: Element | null): node is HTMLElement {
  return node instanceof HTMLElement && node.matches('button, [href], input, select, textarea');
}

function hideOpen(): void {
  if (openModal === null) return;
  openModal.scrim.classList.remove('open');
  openModal = null;
  release?.();
  release = null;
  opener?.focus();
  opener = null;
}

let listenersReady = false;
function ensureListeners(): void {
  if (listenersReady) return;
  listenersReady = true;

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      /**
       * ⚠️ **`a[href]`, ne `[href]`.** Ikone so `<svg><use href="…sprite#id">`
       * in `<use>` ima atribut `href` — `closest('[href]')` zato vrne SVG
       * element namesto gumba pod njim. `focus()` na `<use>` ne naredi nič,
       * fokus se ob zaprtju modala ne vrne in napake ni videti nikjer.
       *
       * `instanceof HTMLElement` je druga varovalka: če selektor kdaj spet
       * ujame SVG, se to pokaže tu in ne šele kot izgubljen fokus.
       */
      const trigger =
        target instanceof Element
          ? target.closest('button, a[href], input, select, textarea')
          : null;
      lastPointerTrigger = trigger instanceof HTMLElement ? trigger : null;
    },
    true,
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideOpen();
  });
}

/* ── Gradnja ───────────────────────────────────────────────────────── */

export function createModal(labelledBy: string): Modal {
  ensureListeners();

  const title = el('h3', { id: labelledBy });
  const close = el('button', { class: 'cl', type: 'button' }, [icon('i-x', 18)]);
  const body = el('div', { class: 'cm-body' });
  const cancel = el('button', { class: 'btn secondary', type: 'button' });
  const confirm = el('button', { class: 'btn primary', type: 'button' });

  const box = el('div', {
    class: 'cm-box',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': labelledBy,
  });
  box.append(
    el('div', { class: 'cm-head' }, [title, close]),
    body,
    el('div', { class: 'cm-foot' }, [cancel, confirm]),
  );

  const scrim = el('div', { class: 'cm-scrim' }, [box]);

  const modal: Modal = {
    scrim,
    box,
    title,
    close,
    body,
    cancel,
    confirm,

    show(focus) {
      // Sprožilca si zapomnimo, da se fokus po zaprtju vrne tja, od koder je
      // uporabnik prišel — sicer skoči na začetek zaslona.
      const active = document.activeElement;
      opener = isControl(active) ? active : lastPointerTrigger;

      /**
       * ⚠️ **Začetni fokus se pove z `autofocus`, ne s `focus()`.**
       *
       * `ds/js/a11y.js` po `trapFocus()` sam postavi fokus na `[autofocus]`
       * ali na prvi fokusabilen element — in to v `setTimeout(0)`, torej
       * **po** našem klicu. Neposreden `focus()` bi bil tiho prepisan z
       * gumbom »zapri«.
       */
      for (const previous of box.querySelectorAll('[autofocus]')) {
        previous.removeAttribute('autofocus');
      }
      focus.setAttribute('autofocus', '');

      scrim.classList.add('open');
      openModal = modal;
      release = trapFocus(box);
    },

    hide: hideOpen,
  };

  close.addEventListener('click', hideOpen);
  cancel.addEventListener('click', hideOpen);
  scrim.addEventListener('click', (event) => {
    // Klik na scrim zapre, klik v škatli ne.
    if (event.target === scrim) hideOpen();
  });

  return modal;
}

/** Polje obrazca: `.field` + `.ftitle` + `.input` (+ `.hint` po potrebi). */
export interface Field {
  wrap: HTMLDivElement;
  label: HTMLLabelElement;
  input: HTMLInputElement;
}

/**
 * ⚠️ **Polje je `.field`, ne `.ff`.** `.ff` iz `base.css` stilira `label` in
 * `input` samodejno, **`.hint` pa je stiliran samo znotraj `.field`**
 * (`components.css:142`). Napaka pod poljem je pri obrazcu za postajo
 * pogosta — podvojen naslov, prazno ime — zato je pomembnejša od udobja
 * samodejnega stiliranja.
 */
export function createField(id: string, type = 'text'): Field {
  const input = el('input', { id, type, class: 'input', autocomplete: 'off' });
  const label = el('label', { class: 'ftitle', for: id });
  return { wrap: el('div', { class: 'field' }, [label, input]), label, input };
}
